import { Helmet } from "react-helmet-async";
import { useLocalePath } from "../hooks/useLocalePath";
import {
  absoluteUrl,
  DEFAULT_COMPANY_NAME,
  isNewMachine,
  machineMetaDescription,
  ORGANIZATION_ID,
  withBrand,
} from "../seoUtils";
import { machineCategoryUrlSlug } from "../slug.js";

export default function MachineJsonLd({ machine }) {
  const { locale } = useLocalePath();
  const isNew = isNewMachine(machine);
  const machineUrl = absoluteUrl(locale, `/machines/${machine.id}`);

  return (
    <Helmet>
      {/* Product Schema with enhanced data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          // Same name and same sentence as the <h1>, the <title> and the meta description —
          // a Product whose schema name disagrees with the page is a rich-result warning.
          name: withBrand(machine),
          image: machine.images[0],
          description: machineMetaDescription(machine),
          brand: {
            "@type": "Brand",
            name: machine.manufacturer,
          },
          sku: String(machine.id),
          mpn: String(machine.id),
          category: machine.category,
          url: machineUrl,
          ...(machine.campaignPrice || machine.price
            ? {
                offers: {
                  "@type": "Offer",
                  url: machineUrl,
                  priceCurrency: machine.currency || "SEK",
                  price: String(machine.campaignPrice || machine.price),
                  availability: "https://schema.org/InStock",
                  itemCondition: isNew
                    ? "https://schema.org/NewCondition"
                    : "https://schema.org/UsedCondition",
                  seller: {
                    "@type": "Organization",
                    "@id": ORGANIZATION_ID,
                    name: DEFAULT_COMPANY_NAME,
                  },
                },
              }
            : {}),
          manufacturer: {
            "@type": "Organization",
            name: machine.manufacturer,
          },
        })}
      </script>

      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Hem",
              item: absoluteUrl(locale, "/"),
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Maskiner",
              item: absoluteUrl(locale, "/catalogue"),
            },
            {
              "@type": "ListItem",
              position: 3,
              name: machine.category,
              item: absoluteUrl(locale, `/catalogue/${machineCategoryUrlSlug(machine)}`),
            },
            {
              "@type": "ListItem",
              position: 4,
              name: withBrand(machine),
              item: machineUrl,
            },
          ],
        })}
      </script>
    </Helmet>
  );
}
